import app from 'new-ui/app';
import { POPUP } from 'utils/constants/popup';
import { INTERVENTIONAL_TYPES_OPTIONS, TREATMENT_TYPE_VALUES } from 'modules/matchesFilter/constants';
import { formatMatchesFilterUrl } from 'modules/matchesFilter/matchesFilter';
import {
  getMatchedSponsoredTreatmentsNctNumbers,
  getMatchedSponsoredTrialsNctNumbers,
  hasMatchedSponsoredTrials,
  hasMatchedSponsoredTreatments,
} from 'modules/matches/helpers';
import {
  ACTION_COMPONENTS,
  ACTION_CONDITION_KEYS,
  ACTION_IDENTIFIER_RENDER_COMPONENT,
  componentMap,
  DISPLAY_CONTEXT,
} from './nba.constants';

const hasAnyNormalizedIdMatch = (conditionArray, contextArray) => {
  if (!Array.isArray(conditionArray) || conditionArray.length === 0) {
    return false;
  }
  const source = Array.isArray(contextArray) ? contextArray : [];
  const normalizedSourceSet = new Set(source.map((n) => String(n).toUpperCase().trim()));
  return conditionArray.some((id) => normalizedSourceSet.has(String(id).toUpperCase().trim()));
};

export const evaluateActionConditions = (conditions, context) => {
  if (!conditions || Object.keys(conditions).length === 0) {
    return true;
  }

  for (const key in conditions) {
    if (Object.prototype.hasOwnProperty.call(conditions, key)) {
      const conditionValue = conditions[key];
      let conditionMet = false;
      switch (key) {
        case ACTION_CONDITION_KEYS.HAVE_BIOMARKER:
          conditionMet = (context.have_biomarker === conditionValue);
          break;
        case ACTION_CONDITION_KEYS.SPONSORED_TREATMENT_NCT_NUMBERS:
          conditionMet = hasAnyNormalizedIdMatch(conditionValue, context.sponsored_treatment_nct_numbers);
          break;
        case ACTION_CONDITION_KEYS.SPONSORED_TRIAL_NCT_NUMBERS:
          conditionMet = hasAnyNormalizedIdMatch(conditionValue, context.sponsored_trial_nct_numbers);
          break;
        case ACTION_CONDITION_KEYS.SELECTED_STANDARD_ONLY:
          conditionMet = (context.selected_standard_only === conditionValue);
          break;
        case ACTION_CONDITION_KEYS.SELECTED_TRIAL:
          conditionMet = (context.selected_trial === conditionValue);
          break;
        case ACTION_CONDITION_KEYS.USER_HAVE_SPONSORED_TRIAL_LIST:
          conditionMet = (context.userHaveSponseredTrialList === conditionValue);
          break;
        case ACTION_CONDITION_KEYS.USER_HAS_SPONSORED_TREATMENTS:
          conditionMet = (context.user_has_sponsored_treatments === conditionValue);
          break;
        case ACTION_CONDITION_KEYS.TRIAL_WITHIN_100_MILES:
          conditionMet = (context.trial_within_100_miles === conditionValue);
          break;
        case ACTION_CONDITION_KEYS.HAS_LOCATION:
          conditionMet = (context.has_location === conditionValue);
          break;
        case ACTION_CONDITION_KEYS.HAS_TARGETED_TRIALS:
          conditionMet = (context.has_targeted_trials === conditionValue);
          break;
        case ACTION_CONDITION_KEYS.HAS_MISSING_CRITICAL_INFO:
          conditionMet = (context.has_missing_critical_info === conditionValue);
          break;
        case ACTION_CONDITION_KEYS.HAS_PHASE_3_TRIALS:
          conditionMet = (context.has_phase_3_trials === conditionValue);
          break;
        case ACTION_CONDITION_KEYS.URL_MATCH:
          if (typeof conditionValue === 'string' && conditionValue.length > 0) {
            const currentUrl = window.location.pathname + window.location.search;
            conditionMet = currentUrl.includes(conditionValue);
          }
          break;
        default:
          console.warn(`Unknown condition key in NBA action: "${key}"`);
          conditionMet = false;
          break;
      }

      if (!conditionMet) {
        return false;
      }
    }
  }

  return true;
};

export const evaluateDisplayContext = (actionDisplayContext, componentDisplayContext) => {
  const values = Array.isArray(actionDisplayContext) ? actionDisplayContext : [];
  if (values.includes(DISPLAY_CONTEXT.ALL)) {
    return true;
  }

  if (!componentDisplayContext) {
    return false;
  }

  return values.includes(componentDisplayContext);
};

export const getVisibleActions = (actionsRaw, evaluationContext, componentDisplayContext) => {
  const allActionsOrdered = (Array.isArray(actionsRaw) ? actionsRaw : []).map((action, index) => ({
    ...action, order: typeof action.order === 'number' ? action.order : index + 1,
  })).sort((a, b) => a.order - b.order);

  return allActionsOrdered
    .filter((action) => (
      evaluateActionConditions(action.conditions, evaluationContext)
      && evaluateDisplayContext(action.displayContext, componentDisplayContext)
    ));
};

export const getTreatmentTypeSelectorProps = ({ commonArgs, setTreatmentTypes }) => ({
  ...commonArgs,
  onSelectionChange: (selectedValues) => {
    setTreatmentTypes(selectedValues);
  },
  initialSelection: commonArgs.initialSelection || 'All',
});

export const getCategoryTypeSelectorProps = ({
  commonArgs,
  setDynamicActionComponentCallback,
  setInterventionalTypes,
  openChatPopup,
}) => {
  const currentParams = new URLSearchParams(window.location.search);
  const initialInterventionalTypes = currentParams.getAll('interventional_types');
  return {
    ...commonArgs,
    categories: INTERVENTIONAL_TYPES_OPTIONS,
    initialSelection: commonArgs.initialSelection || initialInterventionalTypes,
    onApply: (selectedValues) => {
      setInterventionalTypes(selectedValues);
    },
    onLearnMore: () => {
      const message = commonArgs.learnMoreMessage || 'Tell me more about treatment categories.';
      if (typeof setDynamicActionComponentCallback === 'function') {
        setDynamicActionComponentCallback(null);
      }
      openChatPopup({
        popupType: POPUP.ASSISTANCE_CHAT,
        popupData: { preventOverlayClose: true },
        data: { initialUserMessage: message },
      });
    },
  };
};

export const getDynamicActionComponentConfig = ({
  currentAction,
  userWixId,
  sessionData,
  activeMilestoneId,
  selectedStepId,
  setInterventionalTypes,
  setTreatmentTypes,
  setDynamicActionComponentCallback,
  openChatPopup,
}) => {
  if (!currentAction?.renderAnotherComponent) {
    return null;
  }

  const componentName = currentAction.renderAnotherComponent;
  const ComponentToRender = componentMap[componentName];

  if (!ComponentToRender) {
    console.warn(`NBA: Component "${componentName}" for action "${currentAction.name}" not found in componentMap.`);
    return null;
  }

  const contextForProps = {
    userWixId,
    sessionData,
    activeMilestoneId,
    selectedStepId,
    actionName: currentAction.name,
    action: currentAction,
  };

  const componentSpecificArgs = currentAction.renderAnotherComponentArgs || {};
  const generalActionArgs = currentAction.args || {};

  const baseProps = {
    actionData: currentAction,
    context: contextForProps,
    ...generalActionArgs,
    ...componentSpecificArgs,
  };

  let finalProps = baseProps;

  if (componentName === ACTION_COMPONENTS.TREATMENT_TYPE_SELECTOR) {
    finalProps = getTreatmentTypeSelectorProps({
      commonArgs: baseProps,
      setTreatmentTypes,
    });
  } else if (componentName === ACTION_COMPONENTS.CATEGORY_TYPE_SELECTOR) {
    finalProps = getCategoryTypeSelectorProps({
      commonArgs: baseProps,
      setDynamicActionComponentCallback,
      setInterventionalTypes,
      openChatPopup,
    });
  }

  return {
    Component: ComponentToRender,
    props: finalProps,
    originatingActionName: currentAction.name,
  };
};

export const createFrontendActionHandlers = ({
  setDynamicActionComponentRef,
  setInterventionalTypes,
  setTreatmentTypes,
  openChatPopup,
  pushRoute,
}) => ({
  openTinaAndSendMessage: (args, context) => {
    setDynamicActionComponentRef(null);
    const messageToSend = args?.message || `Tell me more about ${context.actionName || 'this action'}.`;

    const actionIdentifier = {
      milestone: context.activeMilestoneId,
      step: context.selectedStepId,
      action: context.actionName,
    };

    openChatPopup({ data: { initialUserMessage: messageToSend, actionIdentifier } });
  },
  TalkWithUs: () => {
    setDynamicActionComponentRef(null);
    app.openPopup(POPUP.CALLUS, { theme: 'middle', preventOverlayClose: true });
  },
  OpenNGS: () => {
    setDynamicActionComponentRef(null);
    app.openPopup(POPUP.UPLOAD, { theme: 'middle', preventOverlayClose: true });
  },
  shareWithDoc: () => {
    setDynamicActionComponentRef(null);
    app.openPopup(POPUP.SHARE_WITH_MY_DOCTOR);
  },
  updateUserState: () => {
    setDynamicActionComponentRef(null);
  },
  answerQuestionsAboutTrialAndTreatment: () => {
    setDynamicActionComponentRef(null);
  },
  updateStep: () => {
    setDynamicActionComponentRef(null);
  },
  determineAndApplyGuideUpdate: () => {
    setDynamicActionComponentRef(null);
  },
  showMatchedTrials: () => {
    setDynamicActionComponentRef(null);
    setTreatmentTypes([TREATMENT_TYPE_VALUES.CLINICAL_TRIALS]);
    pushRoute(formatMatchesFilterUrl(TREATMENT_TYPE_VALUES.CLINICAL_TRIALS));
  },
  showMatchedTreatments: () => {
    setDynamicActionComponentRef(null);
    setTreatmentTypes([TREATMENT_TYPE_VALUES.STANDARD_OF_CARE]);
    pushRoute(formatMatchesFilterUrl(TREATMENT_TYPE_VALUES.STANDARD_OF_CARE));
  },
  [ACTION_IDENTIFIER_RENDER_COMPONENT]: (args, context) => {
    const componentName = args?.componentName;
    const ComponentToRender = componentMap[componentName];
    if (ComponentToRender) {
      const baseProps = { actionData: context.action, context, ...(args?.componentProps || {}) };
      let finalProps = baseProps;
      if (componentName === ACTION_COMPONENTS.TREATMENT_TYPE_SELECTOR) {
        finalProps = getTreatmentTypeSelectorProps({
          commonArgs: baseProps,
          setTreatmentTypes,
        });
      } else if (componentName === ACTION_COMPONENTS.CATEGORY_TYPE_SELECTOR) {
        finalProps = getCategoryTypeSelectorProps({
          commonArgs: baseProps,
          setDynamicActionComponentCallback: setDynamicActionComponentRef,
          setInterventionalTypes,
        });
      }
      setDynamicActionComponentRef({ Component: ComponentToRender, props: finalProps });
    } else {
      console.warn(`Frontend: Component "${componentName}" not found in componentMap for click handler.`);
      setDynamicActionComponentRef(null);
    }
  },
  defaultAction: (identifier, actionName) => {
    console.warn(`Frontend: Default action handler triggered for identifier "${identifier}" (Action: ${actionName}). Opening Tina.`);
    setDynamicActionComponentRef(null);
    const initialUserMessage = `Tell me more about the action: ${actionName}.`;
    openChatPopup({
      popupType: POPUP.ASSISTANCE_CHAT,
      popupData: { preventOverlayClose: true },
      data: { initialUserMessage },
    });
  },
});

export const executeAction = async ({
  actionToExecute,
  frontendActionHandlers,
  context,
}) => {
  const { onActionCallIdentifier: identifier } = actionToExecute;
  if (!identifier || identifier === ACTION_IDENTIFIER_RENDER_COMPONENT) {
    return;
  }

  const handlerKey = typeof identifier === 'string' ? identifier : String(identifier);
  const handler = frontendActionHandlers[handlerKey] || frontendActionHandlers.defaultAction;
  if (typeof handler === 'function') {
    if (handler === frontendActionHandlers.defaultAction) {
      handler(handlerKey, actionToExecute.name, context);
    } else {
      await handler(actionToExecute.args || {}, context);
    }
  } else {
    frontendActionHandlers.defaultAction(handlerKey, actionToExecute.name, context);
  }
};

export const calculateEvaluationContext = ({
  profile, trials, treatments, selectedTreatmentTypes,
}) => {
  const missingInfo = profile.missing_critical_info || {};
  const isStandardOfCareSelected = Array.isArray(selectedTreatmentTypes) && selectedTreatmentTypes.length === 1 && selectedTreatmentTypes[0] === 'standard_of_care';
  const biomarkers = profile.condition_profile?.biomarkers;
  const have_biomarker = Array.isArray(biomarkers) && biomarkers.length > 0;

  return {
    [ACTION_CONDITION_KEYS.USER_HAVE_SPONSORED_TRIAL_LIST]: hasMatchedSponsoredTrials(trials),
    [ACTION_CONDITION_KEYS.USER_HAS_SPONSORED_TREATMENTS]: hasMatchedSponsoredTreatments(treatments),
    [ACTION_CONDITION_KEYS.HAS_PHASE_3_TRIALS]: trials.some((trial) => trial?._phases?.includes(3)),
    [ACTION_CONDITION_KEYS.HAS_MISSING_CRITICAL_INFO]: (
      Object.values(missingInfo).some((isMissing) => isMissing === true)
    ),
    [ACTION_CONDITION_KEYS.HAS_TARGETED_TRIALS]: trials.some((trial) => trial?.groups?.includes('Targeted chemotherapy')),
    [ACTION_CONDITION_KEYS.HAS_LOCATION]: !!profile.info?.country,
    [ACTION_CONDITION_KEYS.TRIAL_WITHIN_100_MILES]: (
      trials.some((trial) => typeof trial.nearest_facility_distance === 'number' && trial.nearest_facility_distance < 100)
    ),
    [ACTION_CONDITION_KEYS.SELECTED_STANDARD_ONLY]: isStandardOfCareSelected,
    [ACTION_CONDITION_KEYS.HAVE_BIOMARKER]: have_biomarker,
    [ACTION_CONDITION_KEYS.SPONSORED_TRIAL_NCT_NUMBERS]: (
      getMatchedSponsoredTrialsNctNumbers(trials || [])
    ),
    [ACTION_CONDITION_KEYS.SPONSORED_TREATMENT_NCT_NUMBERS]: (
      getMatchedSponsoredTreatmentsNctNumbers(treatments || [])
    ),
  };
};

export const getFilteredMilestones = (milestones, evaluationContext, displayContext) => {
  if (!Array.isArray(milestones)) {
    return [];
  }

  return milestones.map((milestone) => {
    const steps = Array.isArray(milestone?.steps) ? milestone.steps : [];

    const filteredSteps = steps.map((step) => {
      const visibleActions = getVisibleActions(step?.actions ?? [], evaluationContext, displayContext);
      if (!visibleActions.length) {
        return null;
      }

      return { ...step, actions: visibleActions };
    }).filter(Boolean);

    if (!filteredSteps.length) {
      return null;
    }

    return { ...milestone, steps: filteredSteps };
  }).filter(Boolean);
};

export const getMilestonesStatus = (filteredMilestones, activeMilestoneId) => {
  if (!activeMilestoneId) {
    return { milestonesForCard: filteredMilestones, completedMilestoneIds: [] };
  }

  const currentMilestoneIndex = filteredMilestones.findIndex((m) => m?.name === activeMilestoneId);

  const completedIds = currentMilestoneIndex > 0
    ? filteredMilestones.slice(0, currentMilestoneIndex).map((m) => m?.name).filter(Boolean)
    : [];

  return { milestonesForCard: filteredMilestones, completedMilestoneIds: completedIds };
};

export const resolveActiveMilestoneAndStep = ({
  milestonesForCard,
  activeMilestoneId,
  selectedStepId,
}) => {
  if (!Array.isArray(milestonesForCard) || milestonesForCard.length === 0) {
    return {
      nextActiveMilestoneId: activeMilestoneId,
      nextSelectedStepId: selectedStepId,
      shouldPersist: false,
      persistPayload: null,
    };
  }

  const activeMilestone = milestonesForCard.find((m) => m?.name === activeMilestoneId);

  if (!activeMilestone) {
    const firstMilestone = milestonesForCard[0];
    const firstStepName = firstMilestone?.steps?.[0]?.name || null;

    return {
      nextActiveMilestoneId: firstMilestone?.name || null,
      nextSelectedStepId: firstStepName,
      shouldPersist: !!(firstMilestone?.name && firstStepName),
      persistPayload: firstMilestone?.name && firstStepName
        ? { milestoneId: firstMilestone.name, stepId: firstStepName, actionName: null }
        : null,
    };
  }

  const stepExists = activeMilestone?.steps?.some((s) => s?.name === selectedStepId);
  if (!stepExists) {
    const firstStepName = activeMilestone?.steps?.[0]?.name || null;

    return {
      nextActiveMilestoneId: activeMilestoneId,
      nextSelectedStepId: firstStepName,
      shouldPersist: !!(activeMilestoneId && firstStepName),
      persistPayload: activeMilestoneId && firstStepName
        ? { milestoneId: activeMilestoneId, stepId: firstStepName, actionName: null }
        : null,
    };
  }

  return {
    nextActiveMilestoneId: activeMilestoneId,
    nextSelectedStepId: selectedStepId,
    shouldPersist: false,
    persistPayload: null,
  };
};

export const getCurrentAction = (
  sessionData,
  activeMilestoneId,
  selectedStepId,
  currentActionName,
  evaluationContext,
  displayContext,
) => {
  if (!sessionData || !activeMilestoneId || !selectedStepId || !currentActionName) {
    return null;
  }

  const currentMilestoneData = sessionData.nbaGuide?.milestones?.find((m) => m.name === activeMilestoneId);
  const currentStepData = currentMilestoneData?.steps?.find((s) => s.name === selectedStepId);
  const visibleActions = getVisibleActions(currentStepData?.actions ?? [], evaluationContext, displayContext);

  return visibleActions.find((a) => a.name === currentActionName) || null;
};
